import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getAuthInstance } from '../../firebase';
import { selectFeature } from '../../selectors/userProfile';
import { useAppSelector } from '../../store/store';
import { getBackendConfig } from '../../utils/backends';
import { CONVOV2_LK_TRANSCRIPT_ROUTE } from './constants';
import type { ScribeRoomCardData, ScribeRoomStatus } from './ScribeRoomCard';
import { ScribeRoomCard } from './ScribeRoomCard';

type LobbyRoom = {
  id: string;
  hostAvaId: string;
  startedAtTs: number;
  conversationMode: string;
  // Optional enriched fields the server may include
  hostAvaName?: string;
  hostUserName?: string;
  requestStartTs?: number;
  status?: ScribeRoomStatus;
  subType?: string;
  remainingCreditMinutes?: number;
  speechLanguage?: string;
  translationLanguage?: string;
  boostedWords?: string[];
  activeScribeNames?: string[];
  osInfo?: string;
  avaAppVersion?: string;
  participants?: string[];
};

const STATUS_ORDER: Record<ScribeRoomStatus, number> = { pending: 0, ongoing: 1, finished: 2 };

function toCardData(room: LobbyRoom): ScribeRoomCardData {
  return {
    id: room.id,
    hostAvaId: room.hostAvaId,
    hostAvaName: room.hostAvaName,
    hostUserName: room.hostUserName,
    requestStartTs: room.requestStartTs ?? room.startedAtTs,
    status: room.status ?? 'pending',
    subType: room.subType,
    remainingCreditMinutes: room.remainingCreditMinutes,
    conversationMode: room.conversationMode,
    speechLanguage: room.speechLanguage,
    translationLanguage: room.translationLanguage,
    boostedWords: room.boostedWords,
    activeScribeNames: room.activeScribeNames,
    osInfo: room.osInfo,
    avaAppVersion: room.avaAppVersion,
    participants: room.participants,
  };
}

function sortRooms(rooms: LobbyRoom[], nowTs: number): LobbyRoom[] {
  return [...rooms].sort((a, b) => {
    const statusA = a.status ?? 'pending';
    const statusB = b.status ?? 'pending';
    if (statusA !== statusB) return STATUS_ORDER[statusA] - STATUS_ORDER[statusB];
    const waitA = nowTs - (a.requestStartTs ?? a.startedAtTs);
    const waitB = nowTs - (b.requestStartTs ?? b.startedAtTs);
    return waitB - waitA;
  });
}

export const LKScribeLobby: React.FC = () => {
  const isScribe = useAppSelector(selectFeature('scribe'));
  const [rooms, setRooms] = useState<LobbyRoom[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isScribe) return;

    let es: EventSource | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let retryDelayMs = 2_000;
    let cancelled = false;

    const connect = async (forceTokenRefresh = false) => {
      if (cancelled) return;
      try {
        const token = await getAuthInstance().currentUser?.getIdToken(forceTokenRefresh);
        if (!token) {
          setError('Not authenticated');
          return;
        }
        if (cancelled) return;

        const baseUrl = getBackendConfig('liveKitApiEndpoint') as string;
        const url = `${baseUrl.replace(/\/v1$/, '')}/v1/scribe/lobby?token=${encodeURIComponent(token)}`;
        es = new EventSource(url);

        es.onopen = () => {
          setConnected(true);
          setError(null);
          retryDelayMs = 2_000;
        };

        es.onmessage = (event) => {
          try {
            setRooms(JSON.parse(event.data) as LobbyRoom[]);
          } catch {
            // ignore parse errors
          }
        };

        es.onerror = () => {
          setConnected(false);
          setError('Connection lost — retrying…');
          es?.close();
          es = null;
          if (!cancelled) {
            retryTimeout = setTimeout(() => {
              // Force token refresh on every retry so an expired token doesn't
              // cause a permanent failure loop.
              retryDelayMs = Math.min(retryDelayMs * 2, 30_000);
              void connect(true);
            }, retryDelayMs);
          }
        };
      } catch (err) {
        setError(String(err));
      }
    };

    void connect();

    return () => {
      cancelled = true;
      if (retryTimeout !== null) clearTimeout(retryTimeout);
      es?.close();
    };
  }, [isScribe]);

  if (!isScribe) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>You don&apos;t have access to the scribe lobby.</Typography>
      </Box>
    );
  }

  const nowTs = Date.now();
  const sorted = sortRooms(rooms, nowTs);

  return (
    <Box sx={{ p: 4, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">Scribe Lobby</Typography>
          {!connected && <CircularProgress size={16} />}
        </Box>
        <Button
          data-testid="new-conversation-btn"
          variant="contained"
          onClick={() => navigate(CONVOV2_LK_TRANSCRIPT_ROUTE)}
        >
          New Conversation
        </Button>
      </Box>

      <Typography variant="body2" color={connected ? 'success.main' : 'warning.main'} sx={{ mb: 3 }}>
        {connected ? '● Live' : '○ Connecting…'}
        {error && ` — ${error}`}
      </Typography>

      {sorted.length === 0 ? (
        <Typography color="text.secondary">No rooms requesting a scribe right now.</Typography>
      ) : (
        <Box>
          {sorted.map((room) => (
            <ScribeRoomCard
              key={room.id}
              room={toCardData(room)}
              nowTs={nowTs}
              onEnter={(id) => navigate(`${CONVOV2_LK_TRANSCRIPT_ROUTE}?roomId=${id}&asScribe=true`)}
              onWatch={(id) => navigate(`${CONVOV2_LK_TRANSCRIPT_ROUTE}?roomId=${id}`)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
